import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { MoreHorizontal, Loader2 } from 'lucide-react';
import PropTypes from 'prop-types';

import API from '@/apiRequest';
import Modal from '@/components/common/Modal';
import { showToast } from '@/store/ducks/toasts';
import CommentsIcon from '@/components/common/icons/comments.svg';
import OrderHistory from '@/components/common/icons/order-history.svg';
import renderSvg from '@/utils/renderSvg';
import ColorButton from '@/components/common/elements/ColorButton';
import classes from './style.module.scss';

const OptionsComponent = ({ inspector, orderId, refreshHandler }) => {
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const [loading, setLoading] = useState(false);
  const menuRef = useRef();
  const dispatch = useDispatch();
  const intl = useIntl();

  const toggleMenu = () => {
    setIsMenuVisible(!isMenuVisible);
  };

  const openModal = type => {
    setModalContent(type);
    setIsMenuVisible(false);
    setShowModal(true);
  };

  const closeModal = () => {
    setModalContent('');
    setShowModal(false);
  };

  const saveComment = async () => {
    setLoading(true);
    const observation = document.getElementById('inspectorObservation')?.value;
    try {
      await API.put(`orders/${orderId}/inspectors/${inspector.public_id}/observation?action=${inspector.action}`, {
        observation,
      });
      dispatch(
        showToast({
          type: 'success',
          title: intl.formatMessage({ id: 'SUCCESS' }),
          text: intl.formatMessage({ id: 'INSPECTOR_UPDATED_SUCCESS' }),
          duration: 5000,
        })
      );
      closeModal();
      refreshHandler();
    } catch (err) {
      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: intl.formatMessage({ id: 'UNKNOWN_ERROR' }),
          duration: 5000,
        })
      );
      console.log(err?.response || err);
    } finally {
      setLoading(false);
    }
  };

  const confirmExcludeIspector = async () => {
    setLoading(true);
    try {
      await API.delete(`orders/${orderId}/inspectors/${inspector.public_id}?action=${inspector.action}`);
      dispatch(
        showToast({
          type: 'success',
          title: intl.formatMessage({ id: 'SUCCESS' }),
          text: intl.formatMessage({ id: 'INSPECTOR_DELETE_SUCCESS' }),
          duration: 5000,
        })
      );
      closeModal();
      refreshHandler();
    } catch (err) {
      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: intl.formatMessage({ id: 'INSPECTOR_DELETE_ERROR' }),
          duration: 5000,
        })
      );
      console.log(err?.response || err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const closeOnClickAway = evt => {
      if (menuRef?.current?.contains(evt.target)) return;
      setIsMenuVisible(false);
    };

    const closeOnEsc = evt => {
      if (evt.key !== 'Escape') return;
      setIsMenuVisible(false);
    };

    document.addEventListener('mouseup', closeOnClickAway);
    document.addEventListener('keyup', closeOnEsc);

    return () => {
      document.removeEventListener('mouseup', closeOnClickAway);
      document.removeEventListener('keyup', closeOnEsc);
    };
  }, []);

  return (
    <div className={classes.advancedOptions} ref={menuRef}>
      <button
        type="button"
        className={classes.ellipsisButton}
        onClick={toggleMenu}
      >
        <MoreHorizontal size={14} className="text-[#303030]" />
      </button>

      {isMenuVisible && (
        <ul className={classes.menu}>
          <li>
            <button
              type="button"
              onClick={() => openModal('editComment')}
            >
              {!inspector.observation ? 'Adicionar comentário' : 'Editar comentário'}
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={() => openModal('excludeIspector')}
            >
              Excluir vistoriador executante
            </button>
          </li>
        </ul>
      )}

      {showModal && (
        <Modal onClose={closeModal} extraStyles={{ maxWidth: '450px' }}>
          <div className="max-w-full">
            {modalContent === 'editComment' && (
              <div>
                <div className={classes.headerModal}>
                  {renderSvg(CommentsIcon, {
                    alt: '',
                    className: classes.icon,
                  })}
                  Comentário
                </div>
                <hr />
                <form id="commentForm">
                  <div className={classes.observation}>
                    <textarea
                      id="inspectorObservation"
                      defaultValue={inspector.observation || ''}
                      name="observation"
                      placeholder="Escreva aqui uma anotação..."
                    />
                  </div>
                </form>

                <div className="flex justify-center gap-4">
                  <ColorButton color="red" onClick={closeModal}>
                    <FormattedMessage id="BUTTON.CANCEL" />
                  </ColorButton>
                  <ColorButton color="green" onClick={saveComment} isBusy={loading}>
                    <FormattedMessage id="BUTTON.CONFIRM" />
                  </ColorButton>
                </div>
              </div>
            )}

            {modalContent === 'excludeIspector' && (
              <div>
                <div className={classes.headerModal}>
                  {renderSvg(OrderHistory, {
                    alt: '',
                    className: classes.icon,
                  })}
                  Deseja excluir vistoriador executante?
                </div>
                <hr />
                <div className="flex justify-center gap-4">
                  <ColorButton color="red" onClick={closeModal}>
                    <FormattedMessage id="BUTTON.CANCEL" />
                  </ColorButton>
                  <ColorButton color="green" onClick={confirmExcludeIspector} isBusy={loading}>
                    <FormattedMessage id="BUTTON.CONFIRM" />
                  </ColorButton>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

OptionsComponent.propTypes = {
  inspector: PropTypes.object.isRequired,
  orderId: PropTypes.string.isRequired,
  refreshHandler: PropTypes.func.isRequired,
};

export default OptionsComponent;
